import axios from "axios";
import { useEffect, useState } from "react";
import { Alert, Box, Button, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { QaState } from "./types";

export default function QaOrderDetails({ orderNumber, revision, noteSearch, onLoaded }: {
  orderNumber: string;
  revision: number;
  noteSearch: string;
  onLoaded: (orderNumber: string, state: QaState) => void;
}) {
  const [data, setData] = useState<QaState | null>(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(false);
    axios.get<QaState>("/api/ClosingChecklistQa/Order", { params: { orderNumber } })
      .then(({ data: findings }) => {
        if (cancelled) return;
        setData(findings);
        onLoaded(orderNumber, findings);
      }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [orderNumber, revision, reload, onLoaded]);

  const notes = data?.notes.filter((note) => note.text.toLocaleLowerCase().includes(noteSearch)) || [];

  return <Box sx={{ p: 2, bgcolor: "background.default" }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>Checklist items with QA notes</Typography>
    {data && noteSearch && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      Showing {notes.length} of {data.notes.length} QA notes matching “{noteSearch}”.
    </Typography>}
    {error ? <Alert severity="error" action={<Button onClick={() => setReload((value) => value + 1)}>Retry</Button>}>
      Unable to load the QA notes for this order.
    </Alert> : !data ? <Skeleton variant="rounded" height={100} /> : !data.notes.length ?
      <Typography variant="body2" color="text.secondary">No QA notes remain on this checklist. Refresh the order list.</Typography> :
      !notes.length ? <Typography variant="body2" color="text.secondary">No QA notes match this filter.</Typography> :
      <TableContainer><Table size="small" aria-label={`QA findings for ${orderNumber}`}>
        <TableHead><TableRow>
          <TableCell>Checklist item</TableCell><TableCell>QA note</TableCell><TableCell>Updated by</TableCell><TableCell>Updated on</TableCell>
        </TableRow></TableHead>
        <TableBody>{notes.map((note) => <TableRow key={note.taskId}>
          <TableCell sx={{ minWidth: 220, verticalAlign: "top" }}>
            {note.requirementNumber && <Typography variant="caption" color="text.secondary">{note.requirementNumber}</Typography>}
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{note.taskDescription || "Checklist task"}</Typography>
          </TableCell>
          <TableCell sx={{ minWidth: 300, verticalAlign: "top", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{note.text}</TableCell>
          <TableCell sx={{ verticalAlign: "top" }}>{note.updatedBy}</TableCell>
          <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>{new Date(note.updatedAt).toLocaleString()}</TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer>}
  </Box>;
}
